/**
 * Firebase Configuration
 * Initializes Firebase for push notifications via FCM
 */

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

let app = null;
let messaging = null;
let auth = null;
let recaptchaVerifier = null;
let confirmationResult = null;

/**
 * Check if Firebase is configured
 */
export function isFirebaseConfigured() {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.messagingSenderId &&
    firebaseConfig.appId
  );
}

/**
 * Initialize Firebase app
 */
export async function initializeFirebase() {
  if (!isFirebaseConfigured()) {
    console.warn('Firebase is not configured. Push notifications will be disabled.');
    return null;
  }

  if (app) {
    return app;
  }

  try {
    app = initializeApp(firebaseConfig);
    return app;
  } catch (error) {
    console.error('Failed to initialize Firebase:', error);
    return null;
  }
}

/**
 * Get Firebase Messaging instance
 */
export async function getMessagingInstance() {
  if (messaging) {
    return messaging;
  }

  const supported = await isSupported();
  if (!supported) {
    console.warn('Firebase Messaging is not supported in this browser.');
    return null;
  }

  await initializeFirebase();
  if (!app) {
    return null;
  }

  try {
    messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error('Failed to get messaging instance:', error);
    return null;
  }
}

/**
 * Get FCM token for push notifications
 * @returns {Promise<string|null>} FCM token or null if unavailable
 */
export async function getFCMToken() {
  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance) {
    return null;
  }

  if (!VAPID_KEY) {
    console.warn('VAPID key not configured. Cannot get FCM token.');
    return null;
  }

  try {
    // Request notification permission if not already granted
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    const token = await getToken(messagingInstance, { vapidKey: VAPID_KEY });
    return token;
  } catch (error) {
    console.error('Failed to get FCM token:', error);
    return null;
  }
}

/**
 * Set up foreground message handler
 * @param {Function} callback - Function to call when message received
 */
export async function onForegroundMessage(callback) {
  const messagingInstance = await getMessagingInstance();
  if (!messagingInstance) {
    return () => {};
  }

  return onMessage(messagingInstance, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
}

/**
 * Get Firebase Auth instance
 */
export function getAuthInstance() {
  if (auth) {
    return auth;
  }

  if (!app) {
    initializeFirebase();
  }

  if (app) {
    auth = getAuth(app);
  }

  return auth;
}

/**
 * Set up invisible reCAPTCHA for phone auth
 * Must be called before sendPhoneOtp
 * @param {string} containerId - ID of the container element for reCAPTCHA
 * @param {Object} callbacks - Optional callbacks for reCAPTCHA events
 * @param {Function} callbacks.onVerified - Called when user completes verification
 * @param {Function} callbacks.onExpired - Called when verification expires
 * @param {Function} callbacks.onError - Called on verification error
 */
export async function setupRecaptcha(containerId = 'recaptcha-container', callbacks = {}) {
  const authInstance = getAuthInstance();
  if (!authInstance) {
    throw new Error('Firebase Auth not initialized');
  }

  // If we already have a valid verifier, reuse it
  if (recaptchaVerifier) {
    console.log('[Firebase] Reusing existing reCAPTCHA verifier');
    return recaptchaVerifier;
  }

  // Check if container exists
  const container = document.getElementById(containerId);
  if (!container) {
    throw new Error(`reCAPTCHA container #${containerId} not found`);
  }

  // Clear any existing reCAPTCHA widgets in the container
  container.innerHTML = '';

  // Wait a tick for DOM to be ready
  await new Promise((resolve) => setTimeout(resolve, 100));

  try {
    // Use 'normal' size for debugging - shows visible checkbox
    // Change back to 'invisible' once working
    recaptchaVerifier = new RecaptchaVerifier(authInstance, containerId, {
      size: 'normal',
      callback: (token) => {
        console.log('[Firebase] reCAPTCHA solved, token received');
        if (callbacks.onVerified) {
          callbacks.onVerified(token);
        }
      },
      'expired-callback': () => {
        console.log('[Firebase] reCAPTCHA expired');
        recaptchaVerifier = null;
        if (callbacks.onExpired) {
          callbacks.onExpired();
        }
      },
      'error-callback': (error) => {
        console.error('[Firebase] reCAPTCHA error:', error);
        recaptchaVerifier = null;
        if (callbacks.onError) {
          callbacks.onError(error);
        }
      },
    });

    // Render the reCAPTCHA widget
    const widgetId = await recaptchaVerifier.render();
    console.log('[Firebase] reCAPTCHA rendered successfully, widgetId:', widgetId);

    return recaptchaVerifier;
  } catch (error) {
    console.error('[Firebase] Error setting up reCAPTCHA:', error);
    recaptchaVerifier = null;
    throw error;
  }
}

/**
 * Send OTP to phone number via Firebase
 * @param {string} phoneNumber - Phone number with country code (e.g., +1234567890)
 * @returns {Promise<{verificationId: string}>}
 */
export async function sendPhoneOtp(phoneNumber) {
  const authInstance = getAuthInstance();
  if (!authInstance) {
    throw new Error('Firebase Auth not initialized');
  }

  if (!recaptchaVerifier) {
    throw new Error('reCAPTCHA not set up. Call setupRecaptcha first.');
  }

  try {
    console.log('[Firebase] Sending OTP to:', phoneNumber);
    console.log('[Firebase] reCAPTCHA verifier:', recaptchaVerifier ? 'exists' : 'null');

    confirmationResult = await signInWithPhoneNumber(authInstance, phoneNumber, recaptchaVerifier);

    // Store verification ID in session storage for persistence
    if (confirmationResult.verificationId) {
      sessionStorage.setItem('firebase_verification_id', confirmationResult.verificationId);
    }

    console.log('[Firebase] OTP sent successfully');
    return {
      verificationId: confirmationResult.verificationId,
    };
  } catch (error) {
    console.error('[Firebase] Error sending OTP:', {
      code: error.code,
      message: error.message,
      fullError: error,
    });

    // Reset reCAPTCHA on error
    if (recaptchaVerifier) {
      try {
        recaptchaVerifier.clear();
      } catch (e) {
        console.log('[Firebase] Error clearing reCAPTCHA after failure:', e);
      }
      recaptchaVerifier = null;
    }

    // Provide user-friendly error messages
    if (error.code === 'auth/invalid-phone-number') {
      throw new Error('Invalid phone number format. Please check and try again.');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many attempts. Please try again later.');
    } else if (error.code === 'auth/quota-exceeded') {
      throw new Error('SMS quota exceeded. Please try again later.');
    } else if (error.code === 'auth/error-code:-39' || error.message?.includes('-39')) {
      // This is a reCAPTCHA verification error
      console.error('[Firebase] reCAPTCHA verification failed. Check Firebase Console settings.');
      throw new Error('Verification failed. Please refresh the page and try again.');
    }

    throw new Error(error.message || 'Failed to send verification code.');
  }
}

/**
 * Verify OTP code via Firebase
 * @param {string} otp - The OTP code entered by user
 * @param {string} [verificationId] - Optional verification ID (uses stored value if not provided)
 * @returns {Promise<{verified: boolean, user?: any}>}
 */
export async function verifyPhoneOtp(otp, verificationId = null) {
  const authInstance = getAuthInstance();
  if (!authInstance) {
    throw new Error('Firebase Auth not initialized');
  }

  const storedVerificationId = verificationId || sessionStorage.getItem('firebase_verification_id');

  if (!storedVerificationId && !confirmationResult) {
    throw new Error('No verification session found. Please request a new code.');
  }

  try {
    let userCredential;

    if (confirmationResult) {
      // Use confirmation result directly if available
      userCredential = await confirmationResult.confirm(otp);
    } else {
      // Fall back to using stored verification ID
      const credential = PhoneAuthProvider.credential(storedVerificationId, otp);
      userCredential = await signInWithCredential(authInstance, credential);
    }

    console.log('[Firebase] OTP verified successfully');

    // Clear stored verification ID
    sessionStorage.removeItem('firebase_verification_id');
    confirmationResult = null;

    return {
      verified: true,
      user: userCredential.user,
    };
  } catch (error) {
    console.error('[Firebase] Error verifying OTP:', error);

    if (error.code === 'auth/invalid-verification-code') {
      throw new Error('Invalid verification code. Please try again.');
    } else if (error.code === 'auth/code-expired') {
      throw new Error('Verification code expired. Please request a new one.');
    }

    throw new Error(error.message || 'Verification failed.');
  }
}

/**
 * Clear phone auth session
 */
export function clearPhoneAuthSession() {
  sessionStorage.removeItem('firebase_verification_id');
  confirmationResult = null;
  if (recaptchaVerifier) {
    recaptchaVerifier.clear();
    recaptchaVerifier = null;
  }
}

/**
 * Check if there's an active Firebase phone auth session
 */
export function hasActivePhoneAuthSession() {
  return !!(confirmationResult || sessionStorage.getItem('firebase_verification_id'));
}

export { getToken };
